import { atom } from "jotai";
import type { participantTypes } from "@pineseed/spec";
import {
  // secureStorage,
  createSecureStorage,
  SecureStorageKeys,
} from "@pineseed/gui/util/secure-storage";

const secureStorage = createSecureStorage();

/**
 * Participant state atoms
 * STRICT RULE: This module contains ONLY atoms - no business logic, no UI code, no external system calls
 */

type ParticipantId = participantTypes["ParticipantId"];

// Internal atom for in-memory storage
const _participantIdAtom = atom<ParticipantId | null>(null);

// Participant ID atom - SECURE STORAGE
export const participantIdAtom = atom(
  (get) => get(_participantIdAtom),
  async (_get, set, newValue: ParticipantId | null) => {
    if (newValue) {
      await secureStorage.setItem(
        SecureStorageKeys.PARTICIPANT_ID,
        JSON.stringify(newValue),
      );
    } else {
      await secureStorage.removeItem(SecureStorageKeys.PARTICIPANT_ID);
    }
    set(_participantIdAtom, newValue);
  },
);

// Load participant ID from secure storage on app start
// If not found, generate a new one using the fingerprinting system
export const loadParticipantIdAtom = atom(null, async (_get, set) => {
  try {
    set(participantLoadingAtom, true);
    set(participantErrorAtom, null);

    const stored = await secureStorage.getItem(
      SecureStorageKeys.PARTICIPANT_ID,
    );

    if (stored) {
      const participantId = JSON.parse(stored) as ParticipantId;
      set(_participantIdAtom, participantId);
      console.log("🔑 Loaded existing participant ID:", participantId.id);
    } else {
      // No stored ID - generate a new one
      console.log("🔑 No stored participant ID - generating new one...");
      const { getParticipantId } = await import("@pineseed/gui/participant");
      const newParticipantId = await getParticipantId();
      set(_participantIdAtom, newParticipantId);
      console.log("🔑 Generated new participant ID:", newParticipantId.id);
    }
  } catch (error) {
    console.error("Failed to load/generate participant ID:", error);
    set(
      participantErrorAtom,
      error instanceof Error ? error : new Error(String(error)),
    );
  } finally {
    set(participantLoadingAtom, false);
  }
});

// Loading state atom
export const participantLoadingAtom = atom(false);

// Error state atom
export const participantErrorAtom = atom<Error | null>(null);

// Derived atom for ready state
export const participantReadyAtom = atom((get) => {
  const participantId = get(participantIdAtom);
  const isLoading = get(participantLoadingAtom);
  const error = get(participantErrorAtom);

  return !isLoading && !error && participantId !== null;
});

// Write-only atom for setting participant ID (for existing code compatibility)
export const setParticipantIdAtom = atom(
  null,
  (_get, set, participantId: ParticipantId | null) => {
    set(participantIdAtom, participantId);
  },
);

// Derived atom for device fingerprint (for existing code compatibility)
export const deviceFingerprintAtom = atom((get) => {
  const participantId = get(participantIdAtom);
  return participantId?.deviceFingerprint || "";
});

// Alias for existing code compatibility
export const currentParticipantIdAtom = participantIdAtom;
